import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import QRCode from 'qrcode'
import CodeForm from './CodeForm'
import { groupSecret } from './codes'

/**
 * VH-285 — set up the authenticator: the QR code, the same secret as text,
 * and one code from the app, which makes it active (FinanceHub FH-104).
 *
 * The QR is drawn here, in the browser, from what the server sent: the secret
 * travels nowhere else. The app names the entry after the environment
 * ("VitaHub Prod", "VitaHub UAT" …), so several of them stay apart.
 *
 * `send(code)` activates; `onDone(data)` gets its answer (the recovery codes).
 */
export default function SecondFactorSetup({ setup, send, onDone, onExpired, children }) {
  const { t } = useTranslation()
  const [qr, setQr] = useState(null)

  useEffect(() => {
    let current = true
    QRCode.toDataURL(setup.otpauth_uri, { margin: 1, width: 220 })
      .then((url) => current && setQr(url))
      .catch(() => current && setQr(null))
    return () => {
      current = false
    }
  }, [setup.otpauth_uri])

  return (
    <section aria-labelledby="setup-title" className="space-y-4">
      <h2 id="setup-title" className="text-base font-semibold text-gray-900">
        {t('secondFactor.setup.title')}
      </h2>
      <ol className="list-decimal space-y-1 pl-5 text-sm text-gray-700">
        <li>{t('secondFactor.setup.step1')}</li>
        <li>{t('secondFactor.setup.step2', { issuer: setup.issuer })}</li>
        <li>{t('secondFactor.setup.step3')}</li>
      </ol>
      <div className="flex justify-center">
        {qr ? (
          <img src={qr} alt={t('secondFactor.setup.qrAlt')} className="h-56 w-56 rounded border border-gray-200" />
        ) : (
          <div className="h-56 w-56 animate-pulse rounded bg-gray-100" aria-hidden="true" />
        )}
      </div>
      <div className="rounded-md bg-gray-50 p-3 text-sm">
        <p className="text-gray-700">{t('secondFactor.setup.byHand')}</p>
        <p className="mt-1 select-all break-all font-mono text-gray-900" data-testid="setup-secret">
          {groupSecret(setup.secret)}
        </p>
        <p className="mt-1 text-xs text-gray-500">
          {t('secondFactor.setup.entry', { issuer: setup.issuer, account: setup.account })}
        </p>
      </div>
      <CodeForm
        idPrefix="setup"
        allowRecovery={false}
        send={({ code }) => send(code)}
        onDone={onDone}
        onExpired={onExpired}
        submitLabel={t('secondFactor.setup.activate')}
      >
        {children}
      </CodeForm>
    </section>
  )
}
